import React, { useState, useEffect } from 'react';

const DesistementsComponent = ({ anneeScolaire = '2025/2026' }) => {
  const [desistements, setDesistements] = useState({
    total: 0,
    parRaison: {},
    parType: { fi: 0, ta: 0, executive: 0 },
    parFiliere: {},
    evolution: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const determinerTypeFormation = (etudiant) => {
    const tf = etudiant.typeFormation;

    if (tf) {
      if (tf === 'LICENCE_PRO') return 'Executive';
      if (tf === 'MASTER_PRO') return 'Executive';

      if (tf === 'CYCLE_INGENIEUR') {
        const niveau = (etudiant.niveauFormation || '').toLowerCase();
        const cycle = (etudiant.cycle || '').toLowerCase();

        if (niveau.includes('executive') || cycle.includes('executive')) return 'Executive';
        if (niveau.includes('ta') || niveau.includes('altern') || cycle.includes('ta') || cycle.includes('altern') || niveau === 'ta') {
          return 'TA';
        }
        return 'FI';
      }

      if (tf === 'MASI' || tf === 'IRM') {
        const niveau = (etudiant.niveauFormation || '').toLowerCase();
        if (niveau.includes('executive')) return 'Executive';
        if (niveau.includes('ta') || niveau.includes('altern') || niveau === 'ta') return 'TA';
        return 'FI';
      }

      if (tf === 'Executive' || tf === 'TA' || tf === 'FI') return tf;
    }

    const niveau = (etudiant.niveauFormation || '').toLowerCase();
    const cycle = (etudiant.cycle || '').toLowerCase();

    if (niveau.includes('executive') || cycle.includes('executive')) return 'Executive';
    if (niveau.includes('ta') || niveau.includes('altern') || cycle.includes('ta') || cycle.includes('altern') || niveau === 'ta') {
      return 'TA';
    }

    return 'FI';
  };

  const fetchDesistements = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token non trouvé. Veuillez vous reconnecter.');
      }

      const response = await fetch('    https://vmi1977988.contaboserver.net/api2/admin/etudiants/all', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const etudiantsData = await response.json();

      const etudiantsFiltres = anneeScolaire === 'toutes'
        ? etudiantsData.filter(e => e.isPartner !== true)
        : etudiantsData.filter(e => e.anneeScolaire === anneeScolaire && e.isPartner !== true);

      const etudiantsDesistes = etudiantsFiltres.filter(e => e.desistement?.statut === true);

      const stats = {
        total: etudiantsDesistes.length,
        parRaison: {},
        parType: { fi: 0, ta: 0, executive: 0 },
        parFiliere: {},
        evolution: []
      };

      etudiantsDesistes.forEach(etudiant => {
        const raison = etudiant.desistement.raison || 'Non spécifiée';
        stats.parRaison[raison] = (stats.parRaison[raison] || 0) + 1;

        const type = determinerTypeFormation(etudiant);
        if (type === 'FI' || type === 'CYCLE_INGENIEUR') {
          stats.parType.fi += 1;
        } else if (type === 'Executive') {
          stats.parType.executive += 1;
        } else if (type === 'TA') {
          stats.parType.ta += 1;
        }

        const filiere = etudiant.filiere || 'Non définie';
        stats.parFiliere[filiere] = (stats.parFiliere[filiere] || 0) + 1;
      });

      if (anneeScolaire !== 'toutes' && etudiantsDesistes.length > 0) {
        const parMois = {};
        etudiantsDesistes.forEach(etudiant => {
          if (etudiant.desistement.dateDesistement) {
            const date = new Date(etudiant.desistement.dateDesistement);
            const mois = date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short' });
            parMois[mois] = (parMois[mois] || 0) + 1;
          }
        });
        stats.evolution = Object.entries(parMois)
          .map(([mois, count]) => ({ mois, count }))
          .sort((a, b) => new Date(a.mois) - new Date(b.mois));
      }

      setDesistements(stats);

    } catch (err) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesistements();
  }, [anneeScolaire]);

  // Fonction pour exporter en Excel
  const exportToExcel = () => {
    let csvContent = '\uFEFF'; // UTF-8 BOM pour Excel

    // En-tête du rapport
    csvContent += 'RAPPORT DES DÉSISTEMENTS\n';
    csvContent += `Année scolaire,${anneeScolaire}\n`;
    csvContent += `Date d'export,${new Date().toLocaleDateString('fr-FR')}\n`;
    csvContent += `Total des désistements,${desistements.total}\n`;
    csvContent += '\n';

    // Tableau 1: Par Type de Formation
    csvContent += 'TABLEAU 1 - PAR TYPE DE FORMATION\n';
    csvContent += 'Type,Nombre\n';
    csvContent += `Formation Initiale (FI),${desistements.parType.fi}\n`;
    csvContent += `Travail Alterné (TA),${desistements.parType.ta}\n`;
    csvContent += `Executive,${desistements.parType.executive}\n`;
    csvContent += `TOTAL,${desistements.parType.fi + desistements.parType.ta + desistements.parType.executive}\n`;
    csvContent += '\n\n';

    // Tableau 2: Raisons des Désistements
    if (Object.keys(desistements.parRaison).length > 0) {
      csvContent += 'TABLEAU 2 - RAISONS DES DÉSISTEMENTS\n';
      csvContent += 'Raison,Nombre,Pourcentage\n';
      Object.entries(desistements.parRaison)
        .sort(([, a], [, b]) => b - a)
        .forEach(([raison, count]) => {
          const percentage = ((count / desistements.total) * 100).toFixed(1);
          csvContent += `"${raison}",${count},${percentage}%\n`;
        });
      csvContent += `TOTAL,${desistements.total},100%\n`;
      csvContent += '\n\n';
    }

    // Tableau 3: Désistements par Filière
    csvContent += 'TABLEAU 3 - DÉSISTEMENTS PAR FILIÈRE\n';
    csvContent += 'Filière,Nombre de désistements,Pourcentage\n';
    Object.entries(desistements.parFiliere)
      .sort(([, a], [, b]) => b - a)
      .forEach(([filiere, count]) => {
        const percentage = ((count / desistements.total) * 100).toFixed(1);
        csvContent += `"${filiere}",${count},${percentage}%\n`;
      });
    csvContent += `TOTAL GÉNÉRAL,${desistements.total},100%\n`;
    csvContent += '\n\n';

    // Tableau 4: Évolution Mensuelle
    if (desistements.evolution.length > 0) {
      csvContent += 'TABLEAU 4 - ÉVOLUTION MENSUELLE\n';
      csvContent += 'Mois,Nombre de désistements\n';
      desistements.evolution.forEach(item => {
        csvContent += `${item.mois},${item.count}\n`;
      });
      const totalEvolution = desistements.evolution.reduce((sum, item) => sum + item.count, 0);
      csvContent += `TOTAL,${totalEvolution}\n`;
      csvContent += '\n\n';
    }

    // Résumé final
    csvContent += 'RÉSUMÉ GÉNÉRAL\n';
    csvContent += 'Indicateur,Valeur\n';
    csvContent += `Total des désistements,${desistements.total}\n`;
    csvContent += `Nombre de filières concernées,${Object.keys(desistements.parFiliere).length}\n`;
    csvContent += `Nombre de raisons différentes,${Object.keys(desistements.parRaison).length}\n`;

    // Créer et télécharger le fichier
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Rapport_Desistements_${anneeScolaire.replace('/', '-')}_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', border: '1px solid #000' }}>
        <p style={{ fontWeight: 'bold' }}>Erreur: {error}</p>
      </div>
    );
  }

  if (desistements.total === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Aucun désistement pour l'année {anneeScolaire}</p>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      padding: '2rem',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Titre */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #000',
        paddingBottom: '0.5rem'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: 'bold',
          color: '#000',
          margin: 0
        }}>
          Étudiants Désistés - Total: {desistements.total}
        </h2>
        <button
          onClick={exportToExcel}
          style={{
            padding: '0.5rem 1rem',
            background: '#217346',
            color: '#fff',
            border: '1px solid #1a5c37',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem'
          }}
          onMouseOver={(e) => e.target.style.background = '#1a5c37'}
          onMouseOut={(e) => e.target.style.background = '#217346'}
        >
          📊 Exporter Excel
        </button>
      </div>

      {/* Statistiques par type */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{
          fontSize: '1rem',
          fontWeight: 'bold',
          marginBottom: '0.75rem',
          color: '#000'
        }}>
          Par Type de Formation
        </h3>
        <table style={{
          width: '100%',
          maxWidth: '500px',
          borderCollapse: 'collapse',
          border: '1px solid #000'
        }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{
                padding: '0.5rem',
                textAlign: 'left',
                border: '1px solid #000',
                fontWeight: 'bold'
              }}>Type</th>
              <th style={{
                padding: '0.5rem',
                textAlign: 'center',
                border: '1px solid #000',
                fontWeight: 'bold'
              }}>Nombre</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '0.5rem', border: '1px solid #000' }}>FI</td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                {desistements.parType.fi}
              </td>
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000' }}>TA</td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                {desistements.parType.ta}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '0.5rem', border: '1px solid #000' }}>Executive</td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                {desistements.parType.executive}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Raisons */}
      {Object.keys(desistements.parRaison).length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 'bold',
            marginBottom: '0.75rem',
            color: '#000'
          }}>
            Raisons des Désistements
          </h3>
          <table style={{
            width: '100%',
            maxWidth: '600px',
            borderCollapse: 'collapse',
            border: '1px solid #000'
          }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'left',
                  border: '1px solid #000',
                  fontWeight: 'bold'
                }}>Raison</th>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'center',
                  border: '1px solid #000',
                  fontWeight: 'bold'
                }}>Nombre</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(desistements.parRaison)
                .sort(([, a], [, b]) => b - a)
                .map(([raison, count], index) => (
                  <tr key={raison} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                      {raison}
                    </td>
                    <td style={{
                      padding: '0.5rem',
                      border: '1px solid #000',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      {count}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Par Filière */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{
          fontSize: '1rem',
          fontWeight: 'bold',
          marginBottom: '0.75rem',
          color: '#000'
        }}>
          Désistements par Filière
        </h3>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #000'
        }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{
                padding: '0.5rem',
                textAlign: 'left',
                border: '1px solid #000',
                fontWeight: 'bold'
              }}>Filière</th>
              <th style={{
                padding: '0.5rem',
                textAlign: 'center',
                border: '1px solid #000',
                fontWeight: 'bold'
              }}>Nombre</th>
              <th style={{
                padding: '0.5rem',
                textAlign: 'center',
                border: '1px solid #000',
                fontWeight: 'bold'
              }}>Pourcentage</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(desistements.parFiliere)
              .sort(([, a], [, b]) => b - a)
              .map(([filiere, count], index) => {
                const percentage = ((count / desistements.total) * 100).toFixed(1);
                return (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                      {filiere}
                    </td>
                    <td style={{
                      padding: '0.5rem',
                      border: '1px solid #000',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }}>
                      {count}
                    </td>
                    <td style={{
                      padding: '0.5rem',
                      border: '1px solid #000',
                      textAlign: 'center'
                    }}>
                      {percentage}%
                    </td>
                  </tr>
                );
              })}
            <tr style={{ background: '#e0e0e0', fontWeight: 'bold' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                TOTAL
              </td>
              <td style={{
                padding: '0.5rem',
                border: '1px solid #000',
                textAlign: 'center'
              }}>
                {desistements.total}
              </td>
              <td style={{
                padding: '0.5rem',
                border: '1px solid #000',
                textAlign: 'center'
              }}>
                100%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Évolution Mensuelle */}
      {desistements.evolution.length > 0 && (
        <div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 'bold',
            marginBottom: '0.75rem',
            color: '#000'
          }}>
            Évolution Mensuelle
          </h3>
          <table style={{
            width: '100%',
            maxWidth: '500px',
            borderCollapse: 'collapse',
            border: '1px solid #000'
          }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'left',
                  border: '1px solid #000',
                  fontWeight: 'bold'
                }}>Mois</th>
                <th style={{
                  padding: '0.5rem',
                  textAlign: 'center',
                  border: '1px solid #000',
                  fontWeight: 'bold'
                }}>Nombre</th>
              </tr>
            </thead>
            <tbody>
              {desistements.evolution.map((item, index) => (
                <tr key={index} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                    {item.mois}
                  </td>
                  <td style={{
                    padding: '0.5rem',
                    border: '1px solid #000',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}>
                    {item.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DesistementsComponent;